
import { useState, useEffect } from "react";
import { Calendar, Plus, Trash2, Edit, Save, X } from "lucide-react";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface WeeklyGoal {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  week: string; // Format: YYYY-MM-DD (start of the week)
}

interface WeeklyGoalsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WeeklyGoalsDialog({ 
  open, 
  onOpenChange 
}: WeeklyGoalsDialogProps) {
  const [goals, setGoals] = useState<WeeklyGoal[]>([]);
  const [newGoalTitle, setNewGoalTitle] = useState("");
  const [newGoalDescription, setNewGoalDescription] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [editGoalTitle, setEditGoalTitle] = useState("");
  const [editGoalDescription, setEditGoalDescription] = useState("");
  
  const { toast } = useToast();
  
  // Format the date for display
  const formattedWeek = selectedDate ? 
    `Week of ${format(selectedDate, 'MMMM d, yyyy')}` : 
    "Select a week";

  // Load goals from localStorage on component mount
  useEffect(() => {
    const savedGoals = localStorage.getItem("weekly_goals");
    if (savedGoals) {
      try {
        setGoals(JSON.parse(savedGoals));
      } catch (error) {
        console.error("Error parsing saved goals:", error);
        toast({
          title: "Error",
          description: "Could not load your weekly goals",
          variant: "destructive",
        });
      }
    }
  }, []);
  
  // Save goals to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("weekly_goals", JSON.stringify(goals));
  }, [goals]);
  
  // Get goals for the selected week
  const weekKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
  const filteredGoals = goals.filter(goal => goal.week === weekKey);
  
  // Handle adding a new goal
  const handleAddGoal = () => {
    if (!newGoalTitle.trim()) {
      toast({
        title: "Error",
        description: "Goal title cannot be empty",
        variant: "destructive",
      });
      return;
    }
    
    const newGoal: WeeklyGoal = {
      id: uuidv4(),
      title: newGoalTitle,
      description: newGoalDescription,
      completed: false,
      week: weekKey,
    };
    
    setGoals([...goals, newGoal]);
    setNewGoalTitle("");
    setNewGoalDescription("");
    
    toast({
      title: "Goal Added",
      description: "Your weekly goal has been added",
    });
  };
  
  // Handle toggling goal completion
  const handleToggleGoal = (goalId: string) => {
    setGoals(goals.map(goal => 
      goal.id === goalId 
        ? { ...goal, completed: !goal.completed } 
        : goal
    ));
  };
  
  // Handle editing a goal
  const handleEditGoal = (goal: WeeklyGoal) => {
    setEditingGoalId(goal.id);
    setEditGoalTitle(goal.title);
    setEditGoalDescription(goal.description || "");
  };
  
  // Save edited goal
  const handleSaveEdit = () => {
    if (!editGoalTitle.trim()) {
      toast({
        title: "Error",
        description: "Goal title cannot be empty",
        variant: "destructive",
      });
      return;
    }
    
    setGoals(goals.map(goal => 
      goal.id === editingGoalId 
        ? { 
            ...goal, 
            title: editGoalTitle, 
            description: editGoalDescription 
          } 
        : goal
    ));
    
    setEditingGoalId(null);
    
    toast({
      title: "Goal Updated",
      description: "Your weekly goal has been updated",
    });
  };
  
  // Handle deleting a goal
  const handleDeleteGoal = (goalId: string) => {
    setGoals(goals.filter(goal => goal.id !== goalId));
    
    toast({
      title: "Goal Deleted",
      description: "Your weekly goal has been removed",
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Weekly Goals</DialogTitle>
          <DialogDescription>
            Set your goals for the week and track your progress.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 my-4">
          {/* Week Selector */}
          <div className="flex items-center space-x-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {formattedWeek}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Add New Goal Form */}
          <div className="space-y-4 border p-4 rounded-md bg-gray-50 dark:bg-gray-900">
            <h3 className="text-lg font-medium">Add New Goal</h3>
            
            <div className="space-y-2">
              <label htmlFor="goal-title" className="block text-sm font-medium">
                Goal Title
              </label>
              <Input
                id="goal-title"
                value={newGoalTitle}
                onChange={(e) => setNewGoalTitle(e.target.value)}
                placeholder="Enter goal title"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="goal-description" className="block text-sm font-medium">
                Description (Optional)
              </label>
              <Textarea
                id="goal-description"
                value={newGoalDescription}
                onChange={(e) => setNewGoalDescription(e.target.value)}
                placeholder="Add details about your goal"
                className="min-h-[80px]"
              />
            </div>
            
            <Button 
              onClick={handleAddGoal} 
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Goal
            </Button>
          </div>
          
          {/* Goals List */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Goals for {formattedWeek}</h3>
            
            {filteredGoals.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                No goals set for this week. Add some goals above!
              </p>
            ) : (
              <div className="space-y-3">
                {filteredGoals.map((goal) => (
                  <div 
                    key={goal.id}
                    className="border rounded-md p-4 hover:shadow-sm transition-shadow"
                  >
                    {editingGoalId === goal.id ? (
                      <div className="space-y-3">
                        <Input
                          value={editGoalTitle}
                          onChange={(e) => setEditGoalTitle(e.target.value)}
                          className="font-medium"
                        />
                        <Textarea
                          value={editGoalDescription}
                          onChange={(e) => setEditGoalDescription(e.target.value)}
                          className="min-h-[80px]"
                        />
                        <div className="flex justify-end space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingGoalId(null)}
                          >
                            <X className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                          <Button 
                            size="sm"
                            onClick={handleSaveEdit}
                          >
                            <Save className="h-4 w-4 mr-1" /> Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              checked={goal.completed}
                              onChange={() => handleToggleGoal(goal.id)}
                              className="mt-1"
                            />
                            <div className={cn(
                              goal.completed ? "text-gray-500 line-through" : "text-blue-600"
                            )}>
                              <h4 className="font-medium">{goal.title}</h4>
                              {goal.description && (
                                <p className="text-sm mt-1">{goal.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditGoal(goal)}
                              className="px-2 h-8"
                            >
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Edit</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteGoal(goal.id)}
                              className="px-2 h-8 text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete</span>
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
